import { MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import { requiredStyles } from "./BasicTab";
import { conditionOptionMap } from "./ValidationTab";
import DependencyDatePickers from "./DependencyDatePickers";
import { Question } from "../../../types/StudyFormById.types";
import { getDependencyParentValueType } from "./useDependencyParentPicker";

export type DependencyValue = {
  operator?: string;
  textValue?: string;
  numberValue?: number;
  questionChoiceId?: string;
};

type Props = {
  dependency: DependencyValue;
  selectedQuestion?: Question;
  readOnly?: boolean;
  onChange: (key: string, value: string | number) => void;
  onDateChange?: (dateValue: string) => void;
};

const DependencyConditionFields = ({
  dependency,
  selectedQuestion,
  readOnly = false,
  onChange,
  onDateChange,
}: Props) => {
  if (!selectedQuestion) {
    return null;
  }

  const qType = getDependencyParentValueType(selectedQuestion);

  return (
    <Stack
      gap={1}
      direction={"row"}
      alignItems={"flex-start"}
      sx={{ width: "100%", minHeight: "110px" }}
    >
      <Stack gap={1} sx={{ width: "50%" }}>
        <Typography variant="body1" fontWeight={"regular"} sx={requiredStyles}>
          If Question
        </Typography>

        <Select
          readOnly={readOnly}
          fullWidth
          id={"operator"}
          placeholder="Select"
          inputProps={{ "aria-label": "Without label" }}
          value={dependency?.operator || ""}
          onChange={(e) => onChange("operator", e.target.value)}
        >
          {conditionOptionMap[qType]?.map((c) => (
            <MenuItem key={c.value} value={c.value}>
              {c.label}
            </MenuItem>
          ))}
        </Select>
      </Stack>

      <Stack gap={1} sx={{ width: "50%" }}>
        <Typography variant="body1" fontWeight={"regular"} sx={requiredStyles}>
          Value
        </Typography>

        {["text", "randomize", "calculated_field_text"].includes(qType) && (
          <TextField
            fullWidth
            id={"textValue"}
            placeholder="Value"
            value={dependency?.textValue || ""}
            onChange={(e) => onChange("textValue", e.target.value)}
            name={"textValue"}
            InputProps={{ readOnly }}
          />
        )}

        {["date"].includes(selectedQuestion.type) && (
          <DependencyDatePickers
            question={selectedQuestion}
            curr={dependency}
            onDateChange={onDateChange}
          />
        )}

        {["number", "slider", "calculated_field"].includes(qType) && (
          <TextField
            type="number"
            fullWidth
            id={"numberValue"}
            placeholder="Value"
            value={
              typeof dependency?.numberValue === "number"
                ? dependency?.numberValue
                : ""
            }
            onChange={(e) => onChange("numberValue", e.target.value)}
            name={"numberValue"}
            InputProps={{ readOnly }}
          />
        )}

        {["radio", "dropdown", "checkbox"].includes(selectedQuestion.type) && (
          <Select
            readOnly={readOnly}
            fullWidth
            id={"questionChoiceId"}
            placeholder="Select"
            inputProps={{ "aria-label": "Without label" }}
            value={dependency?.questionChoiceId || ""}
            onChange={(e) => onChange("questionChoiceId", e.target.value)}
          >
            {selectedQuestion?.choices?.map((c) => (
              <MenuItem key={c.ref} value={c.ref}>
                {c.label}
              </MenuItem>
            ))}
          </Select>
        )}
      </Stack>
    </Stack>
  );
};

export default DependencyConditionFields;
